import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { ApiClientConstant, ApiConnector, Table } from '@cureskin/api-client';
import { AppConfig } from '../../app/app.config';
import { ConnectionService } from '../../services/connection-service';
import { RegimenLogsViewModal } from './regimen-logs-view';

declare interface RegimenDetail {
  id: string,
  margId: string,
  title: string,
  partOfDay: Array<string>,
  startDate?: Date,
  endDate?: Date,
  differenceBtwStartAndEnd?: string,
  onHold: boolean,
  activeProduct: boolean,
  orders?: Array<any>,
  startDates?: Array<any>,
  endDates?: Array<any>
}

type RegimenType = 'FACE' | 'HAIR';

@Component({
  selector: 'regimen-product-view',
  templateUrl: './regimen-products-view.html',
  styleUrls: ['./regimen-products-view.scss'],
  standalone: false
})
export class RegimenProductsViewComponent implements OnChanges {
  @Input('regimen')
  set onUpdateRegimen(regimen: any) {
    if (this.showInternalToggle && this.regimenArray?.length) return;

    if (!regimen) return;
    this.selectRegimen(regimen);
  }
  @Input('user') user: any;
  @Output() emitRegimen: EventEmitter<any> = new EventEmitter<any>();


  @Input('regimensArray') regimenArray: any[] | null = null;

  @Input() showInternalToggle = false;

  regimen: any;
  selectedRegimenType: RegimenType | null = null;
  actionLog: any;
  concerns: Array<string> = [];
  regimenDetails: Array<RegimenDetail> = [];
  uniqueProductIds: Array<string> = [];
  addOnProducts: Array<{ id: string, title: string, orders?: Array<any> }> = [];
  lastDeliveredRegimen: any;

  constructor(public appConfig: AppConfig,
    private readonly connectionService: ConnectionService,
    private readonly dialog: MatDialog) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.showInternalToggle && (changes['regimenArray'] || changes['showInternalToggle'])) {
      this.initFromRegimenArray();
    }
  }

  ngOnInit(): void {
    if (this.showInternalToggle) {
      this.initFromRegimenArray();
    }
  }

  get hasTwoRegimens(): boolean {
    if (!this.regimenArray?.length) return false;
    const types = this.regimenArray
      .map(r => this.getRegimenType(r))
      .filter(Boolean);
    return new Set(types).size > 1;
  }


  get currentRegimenTitle(): string {
    return this.selectedRegimenType === ApiClientConstant.Regimen.Class.HAIR ? 'Hair Regimen' : 'Face Regimen';
  }

  initFromRegimenArray(): void {
    if (!this.regimenArray?.length) return;

    const face = this.regimenArray.find(r => this.getRegimenType(r) === ApiClientConstant.Regimen.Class.FACE) || null;
    const hair = this.regimenArray.find(r => this.getRegimenType(r) === ApiClientConstant.Regimen.Class.HAIR) || null;

    const chosen = face || hair || this.regimenArray[0];
    this.selectRegimen(chosen);
  }


  onToggleRegimen(type: RegimenType): void {
    if (!this.regimenArray?.length) return;

    const next =
      type === ApiClientConstant.Regimen.Class.FACE
        ? this.regimenArray.find(r => this.getRegimenType(r) === ApiClientConstant.Regimen.Class.FACE)
        : this.regimenArray.find(r => this.getRegimenType(r) === ApiClientConstant.Regimen.Class.HAIR);

    if (!next) return;
    this.selectRegimen(next);
  }

  private selectRegimen(regimen: any): void {
    if (!regimen) return;
    this.reset();

    this.regimen = regimen;
    this.selectedRegimenType = this.getRegimenType(regimen);

    this.updateRegimenDetails();
  }

  private getRegimenType(regimen: any): any {
    if (!regimen) return null;

    const raw = regimen?.get
      ? regimen.get('class')
      : (regimen.class ?? regimen?.attributes?.class);

    const t = (raw || '').toString().toUpperCase();

    if (t === ApiClientConstant.Regimen.Class.HAIR) return ApiClientConstant.Regimen.Class.HAIR;
    if (t === ApiClientConstant.Regimen.Class.FACE) return ApiClientConstant.Regimen.Class.FACE;
    return null;
  }

  reset(): void {
    this.uniqueProductIds = [];
    this.concerns = [];
    this.regimenDetails = [];
    this.addOnProducts = [];
    this.lastDeliveredRegimen = null;
  }

  async updateRegimenDetails(): Promise<void> {
    const regimenId = this.regimen?.get ? this.regimen.get('regimenId') : this.regimen?.regimenId;

    this.regimen = JSON.parse(JSON.stringify(await this.connectionService.findOneRegimen({
      where: { regimenId },
      include: ['products', 'concernsLanguageString'],
      project: ['products.title' as 'products', 'products.quantity' as 'products',
        'products.quantityUnit' as 'products', 'morning', 'night',
        'concernsLanguageString.en' as 'concernsLanguageString',
        'active', 'regimenId', 'products.margId' as 'products'],
    })));
    this.concerns = (this.regimen?.concernsLanguageString || []).map((concern: any) => concern.en);
    (this.regimen?.products || []).forEach((product: any) => {
      const partOfDay = this.getPartOfDay(product, this.regimen);
      const onHold = this.isProductOnHold(product, this.regimen);
      const title = this.getProductDisplayName(product);
      this.regimenDetails.push({
        id: product.objectId,
        margId: product.margId,
        partOfDay,
        title,
        onHold,
        activeProduct: true,
        endDate: moment().toDate(),
      });
    });
    this.uniqueProductIds = this.regimen.products.map((each: any) => each.objectId);
    await this.updateRegimenDetailsFromRegimenHistory();
    await this.updateProductStartDate();
    this.updateDifferenceBtwStartDateAndEndDate();
    await this.fetchAllDeliveredAddOnProducts();
    await this.fetchLastDeliveredRegimen();
    this.emitRegimen.emit(this.regimenDetails);
  }

  getProductDisplayName(product: any): string {
    return `${product.title}`;
  }

  async updateRegimenDetailsFromRegimenHistory(): Promise<void> {
    if (!this.regimen?.products?.length) return;
    const regimenHistory = JSON.parse(JSON.stringify(await this.connectionService.findRegimenHistory({
      where: { regimenId: this.regimen.regimenId, active: true },
      include: ['products'],
      project: ['products.title' as 'products', 'products.quantity' as 'products',
        'products.quantityUnit' as 'products', 'morning', 'night', 'products.margId' as 'products'],
      descending: 'createdAt',
    })));
    regimenHistory.forEach((regimen: any) => {
      regimen.products.forEach((product: any) => {
        const partOfDay = this.getPartOfDay(product, regimen);
        const onHold = this.isProductOnHold(product, regimen);
        if (!this.uniqueProductIds.includes(product.objectId)) {
          this.uniqueProductIds.push(product.objectId);
          const title = this.getProductDisplayName(product);
          this.regimenDetails.push({
            id: product.objectId,
            margId: product.margId,
            partOfDay,
            title,
            onHold,
            activeProduct: false,
            endDate: regimen.createdAt,
          });
        }
      });
    });
  }

  async fetchLastDeliveredRegimen(): Promise<void> {
    const regimenObj = new Table.Regimen();
    regimenObj.id = this.regimen.objectId;
    const isCurrentRegimenEdited = await ApiConnector.count(Table.ActionLog, {
      where: { regimen: regimenObj },
    });
    if (isCurrentRegimenEdited) {
      this.lastDeliveredRegimen = JSON.parse(JSON.stringify(await this.connectionService.findOneOrder({
        where: { stage: ApiClientConstant.Order.Stage.DELIVERED, regimen: regimenObj, user: this.user },
        ascending: 'deliveredOn',
        include: ['products'],
        project: ['products.title' as 'products', 'deliveredOn', 'products.quantity' as 'products',
          'products.quantityUnit' as 'products'],
      })));
    } else {
      this.lastDeliveredRegimen = JSON.parse(JSON.stringify(await this.connectionService.findOneOrder({
        where: { stage: ApiClientConstant.Order.Stage.DELIVERED, user: this.user },
        ascending: 'deliveredOn',
        include: ['products'],
        project: ['products.title' as 'products', 'deliveredOn', 'products.quantity' as 'products',
          'products.quantityUnit' as 'products'],
      })));
    }
  }

  async updateProductStartDate(): Promise<void> {
    const orders: Array<any> = JSON.parse(JSON.stringify(await this.connectionService.findOrders({
      where: {
        user: this.user,
        stage: { $nin: ApiClientConstant.Order.CancelledStages },
      },
      descending: 'deliveredOn',
      project: ['products', 'stage', 'deliveredOn', 'productInfo'],
    })));
    orders.forEach((order: any) => {
      const orderProductMargId = order.productInfo.flatMap((pI: any) => pI.margId.split('|'));
      this.regimenDetails.forEach((detail: RegimenDetail) => {
        if (orderProductMargId.includes(detail.margId)) {
          if (!detail.orders) {
            // eslint-disable-next-line no-param-reassign
            detail.orders = [];
          }
          detail.orders.push({
            stage: order.stage,
            deliveredDate: order.deliveredOn,
            createdDate: order.createdAt,
          });
        }
        if (!detail.startDate && orderProductMargId.includes(detail.margId)) {
          // eslint-disable-next-line no-param-reassign
          detail.startDate = moment(order.deliveredOn).toDate();
        }
      });
    });
  }

  updateDifferenceBtwStartDateAndEndDate(): void {
    this.regimenDetails.forEach((detail_: RegimenDetail) => {
      const detail = detail_;
      const diffInDays = moment(detail.endDate).diff(detail.startDate, 'days');
      if (diffInDays > 30) {
        detail.differenceBtwStartAndEnd = `${moment(detail.endDate).diff(detail.startDate, 'months')} months`;
      } else {
        detail.differenceBtwStartAndEnd = `${moment(detail.endDate).diff(detail.startDate, 'days')} days`;
      }
    });
  }

  async fetchAllDeliveredAddOnProducts(): Promise<void> {
    const allUserProductOrders = JSON.parse(JSON.stringify(await this.connectionService.findOrders({
      where: { user: this.user, stage: { $nin: ApiClientConstant.Order.CancelledStages } },
      include: ['products'],
      project: ['products.isCommonAddon' as 'products', 'products.title' as 'products', 'deliveredOn', 'stage'],
      descending: 'createdAt',
    })));
    const uniqueAddonProductIds = [];
    allUserProductOrders.forEach((order: any) => {
      order.products.forEach((product: any) => {
        if (product.isCommonAddon && !this.uniqueProductIds.includes(product.objectId)) {
          if (uniqueAddonProductIds.includes(product.objectId)) {
            const commonAddonProduct = this.addOnProducts.find((each: any) => each.id === product.objectId);
            commonAddonProduct.orders.push({ stage: order.stage, deliveredDate: order.deliveredOn });
          } else {
            uniqueAddonProductIds.push(product.objectId);
            this.addOnProducts.push({ id: product.objectId,
              title: product.title,
              orders: [{ stage: order.stage, deliveredDate: order.deliveredOn }] });
          }
        }
        if (!product.isCommonAddon && !this.uniqueProductIds.includes(product.objectId)) {
          if (uniqueAddonProductIds.includes(product.objectId)) {
            const commonAddonProduct = this.addOnProducts.find((each: any) => each.id === product.objectId);
            commonAddonProduct.orders.push({ stage: order.stage, deliveredDate: order.deliveredOn });
          } else {
            uniqueAddonProductIds.push(product.objectId);
            this.addOnProducts.push({ id: product.objectId,
              title: product.title,
              orders: [{ stage: order.stage, deliveredDate: order.deliveredOn }] });
          }
        }
      });
    });
  }

  async findRegimenChangeLog(): Promise<void> {
    const regimen = new Table.Regimen();
    regimen.id = this.regimen.objectId;
    this.actionLog = await this.connectionService.getActionLog({ regimen });
  }

  async openChangesPopop(): Promise<void> {
    await this.findRegimenChangeLog();
    this.dialog.open(RegimenLogsViewModal, {
      width: '95%',
      data: { actionLog: this.actionLog },
    });
  }

  getPartOfDay(product: any, regimen: any): Array<string> {
    const partOfDay = [];
    if (regimen.morning.filter((each: any) => each.product.objectId === product.objectId).length) {
      partOfDay.push('morning');
    }
    if (regimen.night.filter((each: any) => each.product.objectId === product.objectId).length) {
      partOfDay.push('night');
    }
    return partOfDay;
  }

  isProductOnHold(product: any, regimen: any): boolean {
    if (this.regimen.morning.filter((each: any) => each.product.objectId === product.objectId && each.product.onHold).length) {
      return true;
    }
    return !!this.regimen.night.filter((each: any) => each.product.objectId === product.objectId && each.product.onHold).length;

  }

  protected ApiClientConstant: typeof ApiClientConstant = ApiClientConstant;
}
